// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Prop, Vue, Watch, Ref } from 'vue-property-decorator';
import { BootstrapBadge, BootstrapButton, BootstrapIcon, BootstrapImage, BootstrapLink } from '../../../dsl/front';
import { ImageRatio } from '../../_contracts';
import { prepareImage } from '../../_filters';
import { ArticleAuthor } from '../ArticleAuthor';
import { PlayIcon } from '../PlayIcon';
import { TileCategory } from './_partials/TileCategory';
import { TileLabels } from './_partials/TileLabels';
import { TileSliderIndicator } from './_partials/TileSliderIndicator';
import { TileSize, TileSizeMobile } from './Tile.contracts';
import { sliderConfig, tileImageMaxWidths, tileSizeClassRegistry, titleLengthThresholds, titleLinesThresholds, titleMaxLinesConfig } from './Tile.config';
/**
 * @author Sebastian Dziechciarz <sebastian.dziechciarz@movecloser.pl>
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 * @author Olga Milczek <olga.milczek@movecloser.pl>
 */
let Tile = class Tile extends Vue {
    constructor() {
        super(...arguments);
        /**
         * Component's UUID.
         */
        this.id = null;
        /**
         * Determines whether the YouTube player has been initialised.
         */
        this.isPlayerInitialised = false;
        /**
         * Delayed background image URL for smooth transitions.
         */
        this.delayedBackgroundImage = '';
        /**
         * Timeout reference for delayed background changes.
         */
        this.backgroundChangeTimeout = null;
    }
    /**
     * Helper function to get title with version support.
     */
    getTitleForArticle(article, titleVersion) {
        if (!titleVersion) {
            return article.title;
        }
        if (typeof article.properties.additionalTitles !== 'object' || article.properties.additionalTitles === null ||
            !article.properties.additionalTitles[titleVersion]) {
            return article.title;
        }
        return article.properties.additionalTitles[titleVersion];
    }
    /**
     * Helper function to determine if title is considered long.
     */
    isTitleLongByLength(title, size) {
        const threshold = titleLengthThresholds[size];
        return title.length > threshold;
    }
    /**
     * Helper function to get maximum lines for title.
     */
    getTitleMaxLinesForTitle(title, size) {
        const isLong = false; // this.isTitleLongByLength(title, size)
        const config = titleMaxLinesConfig[size];
        return isLong ? config.long : config.short;
    }
    /**
     * Helper function to estimate title lines count for CSS classes.
     */
    estimateTitleLinesCount(title, size) {
        if (size !== TileSize.XL) {
            return 1;
        }
        const titleLength = title.length;
        if (titleLength <= titleLinesThresholds.short) {
            return 1;
        }
        else if (titleLength <= titleLinesThresholds.medium) {
            return 2;
        }
        else {
            return 3;
        }
    }
    /**
     * Article's author data.
     */
    get author() {
        var _a;
        if (typeof this.article.author === 'undefined') {
            return;
        }
        return (_a = this.article.author) !== null && _a !== void 0 ? _a : undefined;
    }
    /**
     * Determines whether the article has its corresponding video.
     */
    get hasVideo() {
        var _a;
        if (!((_a = this.article.properties) === null || _a === void 0 ? void 0 : _a.cover)) {
            return false;
        }
        const cover = this.article.properties.cover;
        if (!cover.video) {
            return false;
        }
        return ((typeof cover.video.url === 'string' && cover.video.url.length > 0) ||
            (typeof cover.video.onNetworkUrl === 'string' && cover.video.onNetworkUrl.length > 0) ||
            (!!cover.video.file && typeof cover.video.file === 'object' && cover.video.file.value > 0 &&
                (!!cover.video.file.url || !!cover.video.file.m3u8)));
    }
    get isTitleOnImage() {
        return this.titleOnImage && this.size !== TileSize.S;
    }
    get isMobileSize() {
        return this.mobileSize === TileSizeMobile.TILE_PLUS_TEXT;
    }
    /**
     * Determines whether the title is longer than shorter 🙈
     */
    get isTitleLong() {
        return this.isTitleLongByLength(this.title, this.size);
    }
    /**
     * Gets the article title with version support.
     */
    get title() {
        return this.getTitleForArticle(this.article, this.titleVersion);
    }
    /**
     * Maximum number of lines for the tile's title.
     */
    get titleMaxLines() {
        return this.getTitleMaxLinesForTitle(this.title, this.size);
    }
    /**
     * Estimated number of lines for current title (for CSS classes).
     */
    get titleLinesCount() {
        return this.estimateTitleLinesCount(this.title, this.size);
    }
    /**
     * Estimated number of lines for next title (for CSS classes).
     */
    get nextTitleLinesCount() {
        if (!this.nextArticle) {
            return 1;
        }
        return this.estimateTitleLinesCount(this.nextTitle, this.size);
    }
    get shouldShowPublicationDate() {
        return this.showPublicationDate && this.article.publicationDate !== null;
    }
    /**
     * Determines whether the `<ArticleAuthor>` component should be rendered.
     */
    get showArticleAuthor() {
        return this.showAuthorAvatar || this.showAuthorName || this.showPublicationDate;
    }
    /**
     * The CSS class defining the tile's sizing variant.
     */
    get sizeClass() {
        if (typeof tileSizeClassRegistry[this.size] !== 'string') {
            console.warn(`TileModuleUi.sizeClass(): Provided registry does NOT contain the given key.\nFalling back to the first key in the set, which is [${Object.keys(tileSizeClassRegistry)[0]}].`);
            return Object.values(tileSizeClassRegistry)[0];
        }
        return tileSizeClassRegistry[this.size];
    }
    /**
     * The CSS class for gradient color based on domain.
     */
    get gradientClass() {
        if (!this.gradientColor) {
            return '';
        }
        // Convert domain to CSS class format
        const domain = this.gradientColor.toLowerCase().replace(/\./g, '-');
        return `--gradient-${domain}`;
    }
    goToSlide(index) {
        this.$emit('go-to-slide', index);
    }
    /**
     * Prepares thumbnail image for the given article.
     */
    prepareThumbnailImage(article) {
        if (!article ||
            typeof article.properties.cover !== 'object' || typeof article.properties.cover === null ||
            typeof article.properties.cover.image !== 'object' || article.properties.cover.image === null) {
            return {
                src: '',
                width: null,
                height: null
            };
        }
        let ratio = ImageRatio.Standard169;
        if (this.size === TileSize.XL) {
            ratio = this.isMobile ? ImageRatio.Square : ImageRatio.Wide;
        }
        else if (this.size === TileSize.L && this.isMobile) {
            ratio = ImageRatio.Square;
        }
        const maxWidth = this.isMobile
            ? tileImageMaxWidths.mobile
            : tileImageMaxWidths.desktop[this.size];
        return prepareImage(article.properties.cover.image, ratio, maxWidth, this.isLazy);
    }
    /**
     * Applicable `Image` for the current screen resolution.
     */
    get thumbnail() {
        return this.prepareThumbnailImage(this.article);
    }
    /**
     * Gets the thumbnail for the next article in slider.
     */
    get nextThumbnail() {
        return this.prepareThumbnailImage(this.nextArticle);
    }
    /**
     * Gets the background-image style for the next image.
     */
    get nextImageBackgroundStyle() {
        // Use delayed background image if available, otherwise fall back to current thumbnail
        const backgroundImageUrl = this.delayedBackgroundImage ||
            (this.thumbnail && this.thumbnail.src ? this.thumbnail.src : '');
        if (!backgroundImageUrl) {
            return {};
        }
        return {
            'background-image': `url(${backgroundImageUrl})`,
            'background-size': 'cover',
            'background-position': 'center',
            'background-repeat': 'no-repeat'
        };
    }
    /**
     * Determines whether the slider transition is active.
     */
    get isSliderTransitioning() {
        return !!(this.sliderIndicator && this.sliderIndicator.isTransitioning);
    }
    /**
     * Determines if this tile has slider transitions.
     */
    get hasSliderTransition() {
        return !!(this.sliderIndicator && this.nextArticle);
    }
    /**
     * Next article title.
     */
    get nextTitle() {
        if (!this.nextArticle) {
            return '';
        }
        return this.getTitleForArticle(this.nextArticle, this.titleVersion);
    }
    /**
     * Maximum number of lines for the next title.
     */
    get nextTitleMaxLines() {
        if (!this.nextArticle) {
            return this.titleMaxLines;
        }
        return this.getTitleMaxLinesForTitle(this.nextTitle, this.size);
    }
    getTitleStyle() {
        if (!this.titleRef) {
            return undefined;
        }
        return {
            height: this.titleRef.getBoundingClientRect().height + 'px'
        };
    }
    /**
     * Watches for slider transition changes to handle delayed background updates.
     */
    onSliderIndicatorChange(newValue, oldValue) {
        if (!newValue) {
            return;
        }
        // Clear any existing timeout
        if (this.backgroundChangeTimeout) {
            clearTimeout(this.backgroundChangeTimeout);
            this.backgroundChangeTimeout = null;
        }
        // If transitioning has ended, and we have a next thumbnail, schedule the background change
        if (!newValue.isTransitioning && this.nextThumbnail && this.nextThumbnail.src) {
            const delay = newValue.nextIndex === (oldValue === null || oldValue === void 0 ? void 0 : oldValue.nextIndex) ? sliderConfig.backgroundChangeDelay : 0;
            this.backgroundChangeTimeout = setTimeout(() => {
                var _a;
                this.delayedBackgroundImage = ((_a = this.nextThumbnail) === null || _a === void 0 ? void 0 : _a.src) || '';
            }, delay);
        }
    }
    /**
     * Initialize the delayed background image with current thumbnail on mount.
     */
    mounted() {
        if (this.thumbnail && this.thumbnail.src) {
            this.delayedBackgroundImage = this.thumbnail.src;
        }
    }
    /**
     * Clean up timeouts before component destruction.
     */
    beforeDestroy() {
        if (this.backgroundChangeTimeout) {
            clearTimeout(this.backgroundChangeTimeout);
            this.backgroundChangeTimeout = null;
        }
    }
};
__decorate([
    Prop({
        type: Object,
        required: true
    })
], Tile.prototype, "article", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], Tile.prototype, "fallbackImage", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], Tile.prototype, "isLazy", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], Tile.prototype, "isMobile", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "reverseContent", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showAuthorAvatar", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showAuthorName", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showArticleTags", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showCategory", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showPublicationDate", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "showTitle", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], Tile.prototype, "gradientColor", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: TileSize.S
    })
], Tile.prototype, "size", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: TileSizeMobile.TILE
    })
], Tile.prototype, "mobileSize", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], Tile.prototype, "titleOnImage", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: 'h3'
    })
], Tile.prototype, "titleTag", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], Tile.prototype, "titleVersion", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: false
    })
], Tile.prototype, "isPremium", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], Tile.prototype, "windowService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false,
        default: null
    })
], Tile.prototype, "sliderIndicator", void 0);
__decorate([
    Prop({
        type: Object,
        required: false,
        default: null
    })
], Tile.prototype, "nextArticle", void 0);
__decorate([
    Ref('titleRef')
], Tile.prototype, "titleRef", void 0);
__decorate([
    Watch('sliderIndicator', { deep: true, immediate: true })
], Tile.prototype, "onSliderIndicatorChange", null);
Tile = __decorate([
    Component({
        name: 'Tile',
        components: {
            ArticleAuthor,
            BootstrapBadge,
            BootstrapButton,
            BootstrapIcon,
            BootstrapImage,
            BootstrapLink,
            PlayIcon,
            TileCategory,
            TileLabels,
            TileSliderIndicator
        },
        template: `
    <div class="tile"
         :class="[sizeClass, gradientClass, '--title-lines-'+ titleLinesCount, {
           '--title-below': !isTitleOnImage && isMobileSize,
           '--outlined': typeof article.properties.cover === 'object' && article.properties.cover.extraDistinction,
           '--small-title': isTitleLong,
           '--reverse-content': reverseContent
         }]"
         @mouseenter="$emit('mouseenter', $event)"
         @mouseleave="$emit('mouseleave', $event)">
    <!-- Thumbnail -->
    <figure class="tile__thumbnail">
      <!-- Single image (no slider) -->
      <BootstrapImage src="" v-bind="{ ...thumbnail, fallback: fallbackImage }"
                      class="tile__thumbnail__image" />

      <!-- Images with background next image -->
      <div v-if="hasSliderTransition" class="tile__thumbnail__images-wrapper"
           :class="{ '--crossfading': isSliderTransitioning }"
           :style="nextImageBackgroundStyle">
        <BootstrapImage src="" v-bind="{ ...thumbnail, fallback: fallbackImage }"
                        class="tile__thumbnail__image tile__thumbnail__image-current" />
      </div>

      <figcaption class="tile__thumbnail__content-wrapper" :class="{ '--no-gradient': !isTitleOnImage }">
        <!-- "Play" button -->
        <span v-if="!reverseContent && hasVideo" class="tile__play-btn --disabled">
            <PlayIcon />
          </span>

        <!-- Labels -->
        <TileLabels
          :article="article"
          :show-article-tags="showArticleTags"
        />
        <!-- END | Labels -->

        <!-- Category -->
        <TileCategory
          :article="article"
          :show-category="showCategory"
          :size="size"
        />
        <!-- END | Category -->

        <!-- Title (on top of the image) -->
        <div v-if="showTitle && isTitleOnImage && hasSliderTransition"
             class="tile__title-wrapper"
             :class="{ '--crossfading': isSliderTransitioning }"
             :style="getTitleStyle()"
        >
          <span ref="titleRef" class="tile__title tile__title-current">
            <BootstrapLink :link="{ target: article.urlPath }"
                           :class="{'stretched-link' : !isPlayerInitialised }"
                           v-clamp="titleMaxLines">
              {{ title }}
            </BootstrapLink>
          </span>
          <span v-if="nextArticle" class="tile__title tile__title-next">
            <BootstrapLink :link="{ target: nextArticle.urlPath }"
                           :class="{'stretched-link' : !isPlayerInitialised }"
                           v-clamp="nextTitleMaxLines">
              {{ nextTitle }}
            </BootstrapLink>
          </span>
        </div>

        <BootstrapLink v-else-if="showTitle && isTitleOnImage" :link="{ target: article.urlPath }"
                       class="tile__title"
                       :class="{'stretched-link' : !isPlayerInitialised }"
                       v-clamp="titleMaxLines">
          {{ title }}
        </BootstrapLink>

        <!-- "Play" button -->
        <span v-if="reverseContent && hasVideo" class="tile__play-btn p-0 shadow-none me-2 --disabled">
            <PlayIcon />
          </span>

        <div class="tile__content">
          <!-- Author -->
          <ArticleAuthor v-if="author && showArticleAuthor && isTitleOnImage"
                         v-bind="{ showAuthorName, showPublicationDate: shouldShowPublicationDate }"
                         :author="author"
                         :publicationDate="article.publicationDate"
                         :showAuthorAvatar="showAuthorAvatar"
                         class="tile__author" shortenDate />

          <span v-if="article.sponsored && isTitleOnImage" class="tile__sponsored">Materiał sponsorowany</span>
        </div>

        <!-- Slider progress indicator -->
        <TileSliderIndicator
          :slider-indicator="sliderIndicator"
          @go-to="goToSlide"
        />
        <!-- END | Slider progress indicator -->

      </figcaption>
    </figure>
    <!-- END | Thumbnail -->

    <div class="tile__body">
      <BootstrapLink v-if="showTitle && !isTitleOnImage" :link="{ target: article.urlPath }"
                     class="tile__title" :class="{'stretched-link' : !isPlayerInitialised }"
      >
        <span class="tile__title__content" v-clamp="titleMaxLines">
          <TileLabels
            :article="article"
            :show-article-tags="showArticleTags"
          />

          {{ title }}
        </span>
      </BootstrapLink>
      <span v-if="article.sponsored && !isTitleOnImage" class="tile__sponsored">Materiał sponsorowany</span>
      <ArticleAuthor v-if="author && showArticleAuthor && !isTitleOnImage"
                     v-bind="{ showAuthorName, showPublicationDate: shouldShowPublicationDate }"
                     :author="author"
                     :publicationDate="article.publicationDate"
                     :showAuthorAvatar="showAuthorAvatar"
                     class="tile__author" shortenDate />
      <BootstrapLink v-if="!showTitle" :link="{ target: article.urlPath }"
                     class="tile__title"
                     :class="{'stretched-link' : !isPlayerInitialised }"
                     v-clamp="titleMaxLines" />
    </div>

    </div>
  `
    })
], Tile);
export { Tile };
